import { isTruthy } from 'notable-common';
import { z } from 'zod';
import { FlowBlockValueNumberSchema } from '../../blockValue/numberSchema.js';
import { BaseFlowBlockSchema } from '../block.js';
import { FlowBlockType } from '../type.js';
export var FlowFileConversionFormat;
(function (FlowFileConversionFormat) {
    FlowFileConversionFormat["Base64"] = "Base64";
    FlowFileConversionFormat["Pdf"] = "Pdf";
    FlowFileConversionFormat["Base64ToFile"] = "Base64ToFile";
})(FlowFileConversionFormat || (FlowFileConversionFormat = {}));
export var FlowFileConversionOperation;
(function (FlowFileConversionOperation) {
    FlowFileConversionOperation["Convert"] = "Convert";
    FlowFileConversionOperation["SplitPdf"] = "SplitPdf";
    FlowFileConversionOperation["CombinePdf"] = "CombinePdf";
    FlowFileConversionOperation["ExtractPages"] = "ExtractPages";
})(FlowFileConversionOperation || (FlowFileConversionOperation = {}));
export var FlowFileConversionSplitMode;
(function (FlowFileConversionSplitMode) {
    FlowFileConversionSplitMode["Range"] = "Range";
    FlowFileConversionSplitMode["EveryXPages"] = "EveryXPages";
})(FlowFileConversionSplitMode || (FlowFileConversionSplitMode = {}));
const ConvertConfigSchema = z.object({
    operation: z.literal(FlowFileConversionOperation.Convert),
    fileReference: z.string().optional(),
    base64Payload: z.string().optional(),
    format: z.enum(FlowFileConversionFormat).default(FlowFileConversionFormat.Base64),
});
const SplitPdfConfigSchema = z.object({
    operation: z.literal(FlowFileConversionOperation.SplitPdf),
    fileReference: z.string().min(1),
    splitMode: z.enum(FlowFileConversionSplitMode).default(FlowFileConversionSplitMode.Range),
    pageRanges: z.string().optional(),
    pageCount: FlowBlockValueNumberSchema({
        numberValidationSchema: z.number().int().positive(),
    }).optional(),
});
const CombinePdfConfigSchema = z.object({
    operation: z.literal(FlowFileConversionOperation.CombinePdf),
    fileReferences: z.string().optional(),
});
const MAX_PAGE_NUMBERS_INPUT_LENGTH = 128;
const ExtractPagesConfigSchema = z.object({
    operation: z.literal(FlowFileConversionOperation.ExtractPages),
    fileReference: z.string().min(1),
    pageNumbers: z.string().min(1).max(MAX_PAGE_NUMBERS_INPUT_LENGTH),
});
function withDefaultOperation(value) {
    if ('operation' in value) {
        return value;
    }
    return { ...value, operation: FlowFileConversionOperation.Convert };
}
export const FlowFileConversionConfigSchema = z.preprocess((value) => withDefaultOperation(value), z.discriminatedUnion('operation', [
    ConvertConfigSchema,
    SplitPdfConfigSchema,
    CombinePdfConfigSchema,
    ExtractPagesConfigSchema,
]));
export const FlowFileConversionBlockSchema = BaseFlowBlockSchema.extend({
    type: z.literal(FlowBlockType.FileConversion),
    config: FlowFileConversionConfigSchema.superRefine((config, ctx) => {
        if (config.operation === FlowFileConversionOperation.Convert) {
            if (config.format === FlowFileConversionFormat.Base64ToFile) {
                if (!isTruthy(config.base64Payload)) {
                    ctx.addIssue({
                        code: 'custom',
                        message: 'Please provide a Base64 payload to convert.',
                        path: ['base64Payload'],
                    });
                }
            }
            else if (!isTruthy(config.fileReference?.trim())) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Please provide a file reference to convert.',
                    path: ['fileReference'],
                });
            }
        }
        if (config.operation === FlowFileConversionOperation.SplitPdf) {
            if (config.splitMode === FlowFileConversionSplitMode.Range &&
                !isTruthy(config.pageRanges?.trim())) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Please provide the page ranges to split. e.g. 1-3,5,7-10',
                    path: ['pageRanges'],
                });
            }
            if (config.splitMode === FlowFileConversionSplitMode.EveryXPages &&
                !isTruthy(config.pageCount)) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Please provide the number of pages per file. e.g. 3',
                    path: ['pageCount'],
                });
            }
        }
        if (config.operation === FlowFileConversionOperation.CombinePdf) {
            if (!isTruthy(config.fileReferences?.trim())) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Please provide file references to combine.',
                    path: ['fileReferences'],
                });
            }
        }
        if (config.operation === FlowFileConversionOperation.ExtractPages) {
            if (!isTruthy(config.fileReference.trim())) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Please provide a file reference to extract pages from.',
                    path: ['fileReference'],
                });
            }
            if (!isTruthy(config.pageNumbers.trim())) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Please provide the page numbers to extract. e.g. 1,2,3,5 or 1,3-5,7',
                    path: ['pageNumbers'],
                });
            }
        }
    }),
});
//# sourceMappingURL=config.js.map